import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import type { QuickBooksInvoice, QuickBooksCustomer } from "@/lib/types/quickbooks-unified";
import {
  parseQuickBooksApiError,
  parseQuickBooksEntityListResponse,
  parseQuickBooksEntityResponse,
} from "@/lib/types/quickbooks/api-validation";

export function useQuickBooksEntity(
  entityId: string,
  entityType: "customer" | "invoice",
  realmId?: string
) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: ["quickbooks-entity", entityType, entityId, realmId],
    queryFn: async (): Promise<QuickBooksInvoice | QuickBooksCustomer> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      if (!entityId) {
        throw new Error("Entity ID is required");
      }

      const url = new URL(
        `/api/quickbooks/entities/${entityId}?entityType=${entityType}`,
        window.location.origin
      );
      if (realmId) {
        url.searchParams.set("realmId", realmId);
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorBody: unknown = await response.json().catch(() => ({}));
        const error = parseQuickBooksApiError(errorBody);
        throw new Error(error.error || "Failed to fetch entity details");
      }

      const data: unknown = await response.json();
      return parseQuickBooksEntityResponse(data);
    },
    enabled: !!session?.access_token && !!entityId && entityId.trim() !== "",
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateQuickBooksEntity(entityType: "customer" | "invoice") {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async ({ entityId, entityData }: { entityId: string; entityData: Record<string, unknown> }) => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      const response = await fetch(
        `/api/quickbooks/entities/${entityId}?entityType=${entityType}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            [entityType === "invoice" ? "invoiceData" : "customerData"]: entityData,
          }),
        }
      );

      if (!response.ok) {
        const errorBody: unknown = await response.json().catch(() => ({}));
        const error = parseQuickBooksApiError(errorBody);
        throw new Error(error.error || `Failed to update ${entityType}`);
      }

      return response.json();
    },
    onSuccess: (_, { entityId }) => {
      void queryClient.invalidateQueries({ queryKey: ["quickbooks-entity"] });
      void queryClient.invalidateQueries({ queryKey: ["quickbooks-entities"] });
      toast.success(
        `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} updated successfully`
      );
    },
    onError: (error: Error) => {
      toast.error(`Failed to update ${entityType}: ${error.message}`);
    },
  });
}

export function useCreateQuickBooksEntity(entityType: "customer" | "invoice") {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async (entityData: Record<string, unknown>) => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      const response = await fetch(`/api/quickbooks/entities?entityType=${entityType}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          [entityType === "invoice" ? "invoiceData" : "customerData"]: entityData,
        }),
      });

      if (!response.ok) {
        const errorBody: unknown = await response.json().catch(() => ({}));
        const error = parseQuickBooksApiError(errorBody);
        throw new Error(error.error || `Failed to create ${entityType}`);
      }

      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quickbooks-entities"] });
      toast.success(
        `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} created successfully`
      );
    },
    onError: (error: Error) => {
      toast.error(`Failed to create ${entityType}: ${error.message}`);
    },
  });
}
